import * as React from "react";

export type DesignStudioScope = "global" | "coach";

export interface ScopeCoach {
  id: number;
  label: string;
}

export interface DesignStudioScopeState {
  scope: DesignStudioScope;
  coach: ScopeCoach | null;
  coachId: number | null;
  setScope: (scope: DesignStudioScope) => void;
  setCoach: (coach: ScopeCoach | null) => void;
}

type Snapshot = { scope: DesignStudioScope; coach: ScopeCoach | null };

const INITIAL: Snapshot = { scope: "global", coach: null };
let snapshot: Snapshot = INITIAL;
const listeners = new Set<() => void>();

function update(next: Snapshot) {
  snapshot = next;
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only reset of the admin scope. */
export function resetDesignStudioScope(): void {
  update(INITIAL);
}

export const DesignStudioScopeContext = React.createContext<DesignStudioScopeState | undefined>(undefined);

/**
 * Admin scope (Global | Coach + coach picker). Held in memory so it survives moving between the
 * admin list, New Design, and editor, and resets to Global on reload; it is never put in the URL.
 */
export const DesignStudioScopeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { scope, coach } = React.useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => INITIAL
  );

  const value = React.useMemo<DesignStudioScopeState>(
    () => ({
      scope,
      coach,
      coachId: coach?.id ?? null,
      setScope: nextScope => update({ scope: nextScope, coach: nextScope === "coach" ? snapshot.coach : null }),
      setCoach: nextCoach => update({ scope: nextCoach ? "coach" : snapshot.scope, coach: nextCoach }),
    }),
    [scope, coach]
  );

  return <DesignStudioScopeContext.Provider value={value}>{children}</DesignStudioScopeContext.Provider>;
};

export function useDesignStudioScope(): DesignStudioScopeState {
  const context = React.useContext(DesignStudioScopeContext);
  if (!context) throw new Error("useDesignStudioScope must be used within DesignStudioScopeProvider");
  return context;
}
